import { Sparkles, Send, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/core/api';
import { useAssistantChat } from './queries';
import { useAssistant } from './store';
import type { ChatMessage } from './types';

/** Generate a stable id without depending on globals (jsdom has crypto but
 *  rolldown's coverage build doesn't always polyfill). */
function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Floating "Ask the assistant" button + slide-out drawer. Mounted once in
 *  `AppShell`. Reads the active entity from the assistant store so detail
 *  pages can scope retrieval (Wave 10 — `useAssistantEntity`). */
export function AssistantPanel() {
  const open = useAssistant((s) => s.open);
  const setOpen = useAssistant((s) => s.setOpen);
  const entity = useAssistant((s) => s.entity);
  const messages = useAssistant((s) => s.messages);
  const append = useAssistant((s) => s.appendMessage);
  const clear = useAssistant((s) => s.clearMessages);

  const chat = useAssistantChat();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll the message list to the bottom on new messages.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, open]);

  async function onSend() {
    const question = input.trim();
    if (!question || chat.isPending) return;
    setInput('');

    const userMsg: ChatMessage = { id: newId(), role: 'user', text: question };
    append(userMsg);

    try {
      const result = await chat.mutateAsync({
        question,
        ...(entity ? { entity: entity.entity, entity_id: entity.entity_id } : {}),
      });
      append({
        id: newId(),
        role: 'assistant',
        text: result.answer,
        sources: result.sources,
      });
    } catch (e) {
      const err = e as ApiError;
      const status = err?.status;
      const fallback =
        status === 503
          ? 'The assistant is not configured yet (missing embedder or chat client).'
          : (err?.message ?? 'The assistant failed to answer.');
      append({ id: newId(), role: 'assistant', text: fallback });
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl+Enter or plain Enter (no Shift) submits — Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSend();
    }
  }

  return (
    <>
      {/* Floating action button — bottom-right of the viewport. */}
      <Button
        type="button"
        size="icon"
        className="fixed bottom-6 right-6 z-40 size-12 rounded-full shadow-lg"
        aria-label="Open AI assistant"
        onClick={() => setOpen(true)}
      >
        <Sparkles className="size-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full max-w-md flex-col p-0 sm:max-w-md">
          <SheetHeader className="border-b p-4">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="size-4" />
              AI Assistant
            </SheetTitle>
            <SheetDescription>
              Ask anything about your organization&apos;s knowledge base.
            </SheetDescription>
            {entity ? (
              <div className="pt-1">
                <Badge variant="secondary" className="font-normal">
                  Scoped: {entity.label}
                </Badge>
              </div>
            ) : null}
          </SheetHeader>

          {/* Conversation */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto p-4">
            {messages.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                {entity
                  ? `Ask a question about ${entity.label}.`
                  : 'Ask a question to get started.'}
              </p>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
                {chat.isPending ? (
                  <p className="text-muted-foreground text-xs italic">Thinking…</p>
                ) : null}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="space-y-2 border-t p-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask the assistant… (Shift+Enter for newline)"
              rows={2}
              aria-label="Your question"
            />
            <div className="flex items-center justify-between">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clear}
                disabled={messages.length === 0}
              >
                <Trash2 className="size-4" /> Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void onSend()}
                disabled={chat.isPending || input.trim().length === 0}
              >
                <Send className="size-4" />
                {chat.isPending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
        )}
      >
        {message.text}
        {message.sources && message.sources.length > 0 ? (
          <div className="mt-2 border-t border-current/20 pt-2">
            <p className="text-xs font-medium opacity-70">Sources</p>
            <ul className="mt-1 space-y-1 text-xs">
              {message.sources.map((s) => (
                <li key={s.id}>{s.title || `Chunk #${s.id}`}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
